import { useCallback, useRef, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { getAgentFileUrl, getAgentStreamUrl } from '../../thunks/agent_harness_thunks';
import {
	AgentErrorInfo,
	classifyAgentError,
	humanizeAgentError,
} from '../../utils/AgentErrorUtils';
import {
	AgentConversationInfo,
	AgentModelsResponse,
	AgentPendingApproval,
	AgentSessionInfo,
	AgentStreamEvent,
	AgentTranscriptEntry,
	AgentUploadResponse,
} from '../../types/agentHarness';

export interface AgentChatActions {
	exchangeLogin: () => Promise<AgentSessionInfo>;
	fetchModels: () => Promise<AgentModelsResponse>;
	/** `GET /api/me` — who the harness believes the live session belongs to. */
	fetchIdentity: () => Promise<AgentSessionInfo>;
	createConversation: (model?: string, thinkingLevel?: string) => Promise<AgentConversationInfo>;
	sendMessage: (conversationId: string, messageId: string, text: string) => Promise<void>;
	cancelTurn: (conversationId: string) => Promise<void>;
	setConversationModel: (conversationId: string, model: string) => Promise<void>;
	setThinkingLevel: (conversationId: string, thinkingLevel: string) => Promise<void>;
	resolveApproval: (approvalId: string, approved: boolean) => Promise<void>;
	uploadFile: (file: File) => Promise<AgentUploadResponse>;
}

export type AgentChatStatus = 'connecting' | 'ready' | 'reconnecting' | 'error';

/** `agentJson` puts the HTTP status on the error it throws. */
const isAuthFailure = (error: any): boolean =>
	!!error && (error.status === 401 || error.status === 403);

/**
 * True only when the server has told us the session cannot be used again.
 *
 * The distinction matters on resume: giving up on a stored conversation costs
 * the user their transcript, so it is worth doing only for a failure that
 * proves reopening it is impossible. A network error proves nothing.
 */
const isSessionUnusable = (error: any): boolean =>
	isAuthFailure(error) || (!!error && error.status === 404);

/**
 * Marks an error whose cause is already on screen in the connection strip, so
 * the caller's own catch does not report the same failure a second time.
 */
const markSurfaced = (error: any): any => {
	if (error && typeof error === 'object') {
		error.surfaced = true;
	}
	return error;
};

/**
 * How long to wait for a stream to say `hello` before calling it a failure.
 *
 * Generous: the harness sends `hello` in its first write, so this is only ever
 * reached when the stream genuinely never opened. Shorter, and a slow network
 * reads as a dead connection.
 */
const HELLO_TIMEOUT_MS = 15000;

const mintMessageId = (): string => {
	return 'u-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

let noticeCounter = 0;
const mintNoticeId = (): string => {
	noticeCounter += 1;
	return 'notice-' + noticeCounter;
};

/**
 * All chat state for one agent conversation: the login exchange, the SSE
 * stream, the transcript, approvals, and uploads. Transcript entries are keyed
 * by the ids the harness mints, so replay after a dropped stream (EventSource
 * reconnects with Last-Event-ID automatically) updates rows in place instead
 * of duplicating them.
 */
/** A message whose network call failed, kept so it can be sent again. */
export interface AgentUnsentMessage {
	/** The id it was first sent under — reused so a retry updates that row. */
	id: string;
	text: string;
	/** Humanized reason, or empty when the connection strip already says why. */
	reason: string;
}

/**
 * Unsent messages by id, in the order they were sent.
 *
 * Keyed rather than a single slot so a failure stays attached to *its* message.
 * With one slot, sending a second message after a failure quietly dropped the
 * first one's marker and left it looking delivered when the agent never saw it.
 */
export type AgentUnsentMessages = { [messageId: string]: AgentUnsentMessage };

export interface AgentChatOptions {
	/** Resume this conversation instead of creating one, restoring its transcript. */
	conversationId?: string;
	/** Called whenever the live conversation id changes, for the caller to persist. */
	onConversationId?: (conversationId: string) => void;
	/**
	 * Whether a live harness session may be reused rather than replaced.
	 *
	 * Given what the harness reports for the session it already holds. Answering
	 * false only costs a login exchange; answering true wrongly means running as
	 * whoever the old session belonged to, so the caller should refuse on any
	 * doubt. Supplied by the caller because the comparison needs OpenWrench
	 * identity — account, role, workspace — that this hook has no business
	 * knowing.
	 */
	canReuseSession?: (harnessEmail: string, harnessRole: string) => boolean;
	/** Called after a fresh exchange, so the caller can record whose session it is. */
	onSessionEstablished?: () => void;
}

export const useAgentChat = (actions: AgentChatActions, options?: AgentChatOptions) => {
	const [status, setStatus] = useState<AgentChatStatus>('connecting');
	const [errorInfo, setErrorInfo] = useState<AgentErrorInfo | null>(null);
	const [busy, setBusy] = useState(false);
	// When the current turn began, in epoch ms; 0 while idle. Kept here rather
	// than derived from `busy` because a turn is not one continuous busy stretch:
	// an agentic turn finishes several assistant messages before it is over.
	const [turnStartedAt, setTurnStartedAt] = useState(0);
	const [entries, setEntries] = useState<AgentTranscriptEntry[]>([]);
	const [conversation, setConversation] = useState<AgentConversationInfo | null>(null);
	const [modelsInfo, setModelsInfo] = useState<AgentModelsResponse | null>(null);
	const [pendingUploads, setPendingUploads] = useState<AgentUploadResponse[]>([]);
	const [uploading, setUploading] = useState(false);
	const [unsentMessages, setUnsentMessages] = useState<AgentUnsentMessages>({});

	const eventSourceRef = useRef<EventSource | null>(null);
	// The re-auth path runs from a stream callback that outlives the render it
	// was created in, so it needs the live conversation rather than the one
	// captured at the time.
	const conversationRef = useRef<AgentConversationInfo | null>(null);
	const recoveringRef = useRef(false);
	const loginRef = useRef<Promise<AgentSessionInfo> | null>(null);
	const unmountedRef = useRef(false);
	/**
	 * The conversation `connect` should try to reopen.
	 *
	 * Retry used to call `connect('')`, which always started a fresh chat. That
	 * turned a moment offline into a lost transcript: the stored conversation was
	 * still perfectly resumable, and Try again was what threw it away.
	 */
	const resumeIdRef = useRef(options && options.conversationId ? options.conversationId : '');

	/** Resolves when the stream currently opening says `hello`. */
	const helloWaiterRef = useRef<{ resolve: () => void; reject: (error: any) => void } | null>(null);

	/** Settles the waiter one way and clears it, so a later stream gets its own. */
	const settleHello = useCallback((error?: any) => {
		const waiter = helloWaiterRef.current;
		helloWaiterRef.current = null;
		if (!waiter) {
			return;
		}
		if (error) {
			waiter.reject(error);
		} else {
			waiter.resolve();
		}
	}, []);

	/**
	 * A promise for the next `hello`, armed *before* the stream is opened.
	 *
	 * `openStream` only constructs an `EventSource`; nothing about that says the
	 * connection was accepted. Treating it as readiness let the composer take a
	 * message into a conversation the client could not hear back from, and a
	 * refused stream then replaced the conversation and took the message with it.
	 *
	 * Bounded, because the opposite failure is worse: a panel stuck in
	 * "connecting" for ever, with no error and nothing to retry.
	 */
	const awaitHello = useCallback((): Promise<void> => {
		settleHello(new Error('Superseded by a newer connection'));
		return new Promise<void>((resolve, reject) => {
			const timer = window.setTimeout(() => {
				settleHello(new Error('The assistant did not finish connecting.'));
			}, HELLO_TIMEOUT_MS);
			const done = (settleError?: any) => {
				window.clearTimeout(timer);
				if (settleError) {
					reject(settleError);
				} else {
					resolve();
				}
			};
			helloWaiterRef.current = { resolve: () => done(), reject: (error: any) => done(error) };
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settleHello]);

	/**
	 * Exchanges a login, or joins the one already in flight.
	 *
	 * An expiring session tends to be noticed twice at once — the stream emits
	 * `auth_error` at about the moment the next call 401s — and every exchange
	 * burns one of the harness's MAX_SESSIONS (10 by default). Coalescing means
	 * one lapse costs one slot however many callers trip over it.
	 */
	const exchangeLoginOnce = useCallback((): Promise<AgentSessionInfo> => {
		if (!loginRef.current) {
			const clear = () => {
				loginRef.current = null;
			};
			loginRef.current = actions.exchangeLogin().then(
				(session) => {
					clear();
					return session;
				},
				(loginError) => {
					clear();
					throw loginError;
				}
			);
		}
		return loginRef.current;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actions.exchangeLogin]);

	/** Starts the turn clock, or leaves a running one alone. */
	const startTurnClock = useCallback(() => {
		setTurnStartedAt((current) => current || Date.now());
	}, []);

	const endTurn = useCallback(() => {
		setBusy(false);
		setTurnStartedAt(0);
	}, []);

	const appendNotice = useCallback((severity: 'info' | 'error', text: string) => {
		setEntries((current) => [...current, { kind: 'notice', id: mintNoticeId(), severity, text }]);
	}, []);

	/**
	 * The one place a rejected call turns into a transcript notice. Skips errors
	 * the connection strip already owns, and humanizes the rest — the harness
	 * relays provider payloads verbatim, so a raw `error` field is not
	 * necessarily something a person can read.
	 */
	const appendErrorNotice = useCallback(
		(error: any, fallback: string) => {
			if (error && error.surfaced) {
				return;
			}
			const raw = error instanceof Error ? error.message : '';
			appendNotice('error', raw ? humanizeAgentError(raw) : fallback);
		},
		[appendNotice]
	);

	const upsertApproval = useCallback((approval: AgentPendingApproval) => {
		setEntries((current) => {
			const index = current.findIndex(
				(existing) => existing.kind === 'approval' && existing.id === approval.id
			);
			if (index !== -1) {
				return current;
			}
			return [
				...current,
				{
					kind: 'approval',
					id: approval.id,
					toolCallId: approval.toolCallId,
					toolName: approval.toolName,
					args: approval.args,
					destructive: approval.destructive,
					resolved: false,
					approved: false,
				},
			];
		});
	}, []);

	const handleStreamEvent = useCallback(
		(event: AgentStreamEvent) => {
			switch (event.type) {
				case 'hello': {
					// The stream is live. This is what "ready" means — not that a REST
					// call succeeded — and it is the only signal that arrives together
					// with the replayed transcript.
					settleHello();
					setStatus('ready');
					setErrorInfo(null);
					// `endTurn` rather than `setBusy(false)`: this is the authoritative
					// state, and leaving `turnStartedAt` from the previous turn behind
					// meant the *next* turn inherited its start time and announced
					// itself as slow the moment it began.
					if (event.busy) {
						setBusy(true);
						startTurnClock();
					} else {
						endTurn();
					}
					setConversation((current) =>
						current
							? {
									...current,
									model: event.model,
									thinkingLevel: event.thinkingLevel,
									toolset: event.toolset,
							  }
							: current
					);
					event.pending.forEach(upsertApproval);
					break;
				}
				case 'gap': {
					appendNotice('info', 'Some earlier messages could not be replayed after a reconnect.');
					break;
				}
				case 'user_text': {
					// The server has the message. That settles it even if our POST is
					// still in flight or its response was lost: without this, a dropped
					// response marked an accepted message "Not sent", so the reply
					// streamed in beside a Try again that would have run the turn twice.
					setUnsentMessages((current) => {
						if (!current[event.id]) {
							return current;
						}
						const next = { ...current };
						delete next[event.id];
						return next;
					});
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'user' && existing.id === event.id
						);
						if (index !== -1) {
							return current;
						}
						return [...current, { kind: 'user', id: event.id, text: event.text }];
					});
					break;
				}
				case 'assistant_thinking':
				case 'assistant_text':
				case 'assistant_done': {
					// `idle` ends the turn — not this. An agentic turn completes several
					// assistant messages, each with its own `assistant_done`, before it is
					// actually over; clearing `busy` here blinked the indicator out and
					// restarted the elapsed timer at every step, so it could never reach a
					// minute no matter how long the turn ran.
					if (event.type !== 'assistant_done') {
						setBusy(true);
						startTurnClock();
					}
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'assistant' && existing.id === event.id
						);
						const previous =
							index !== -1
								? (current[index] as { text: string; thinking: string })
								: { text: '', thinking: '' };
						// Assistant events are cumulative snapshots, not deltas: each one
						// replaces the field it carries and preserves the other.
						let text = previous.text;
						let thinking = previous.thinking;
						let done = false;
						if (event.type === 'assistant_thinking') {
							thinking = event.text;
						} else if (event.type === 'assistant_text') {
							text = event.text;
						} else {
							text = event.text;
							thinking = event.thinking;
							done = true;
						}
						const nextEntry: AgentTranscriptEntry = {
							kind: 'assistant',
							id: event.id,
							text,
							thinking,
							done,
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'tool_start': {
					setBusy(true);
					startTurnClock();
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'tool' && existing.id === event.id
						);
						const nextEntry: AgentTranscriptEntry = {
							kind: 'tool',
							id: event.id,
							name: event.name,
							args: event.args,
							summary: '',
							isError: false,
							done: false,
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'tool_end': {
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'tool' && existing.id === event.id
						);
						if (index === -1) {
							return [
								...current,
								{
									kind: 'tool',
									id: event.id,
									name: event.name,
									args: undefined,
									summary: event.summary,
									isError: event.isError,
									done: true,
								},
							];
						}
						const next = current.slice();
						const existing = next[index] as { kind: 'tool'; args: unknown };
						next[index] = {
							kind: 'tool',
							id: event.id,
							name: event.name,
							args: existing.args,
							summary: event.summary,
							isError: event.isError,
							done: true,
						};
						return next;
					});
					break;
				}
				case 'approval_required': {
					upsertApproval(event);
					break;
				}
				case 'approval_resolved': {
					setEntries((current) =>
						current.map((existing) =>
							existing.kind === 'approval' && existing.id === event.id
								? { ...existing, resolved: true, approved: event.approved }
								: existing
						)
					);
					break;
				}
				case 'file': {
					// Not every `file` event is a file for the user. The harness spills
					// any tool result over TOOL_RESULT_MAX_BYTES (64 KB) to
					// `tool-result-<uuid>.json` purely so the model can page it back with
					// read_tool_result, then broadcasts it on the same event as a real
					// export — so an ordinary work-order query produced a 97 KB download
					// card of escaped ToolResult envelope (OPE-5281).
					//
					// The two are told apart by the endpoint that serves them, which is a
					// distinction the harness already makes: deliberate output —
					// `save_file` and sandbox files — is served from `/api/outputs/`,
					// while `/api/tool-results/` is model plumbing with no user-facing
					// meaning. Keying on the route rather than the file name or extension
					// means an artifact cannot be mistaken for an export the user asked
					// for. The harness should stop announcing these at all; until it
					// does, dropping them here keeps them out of the transcript.
					if (event.url.indexOf('/api/tool-results/') === 0) {
						break;
					}
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'file' && existing.id === event.id
						);
						const nextEntry: AgentTranscriptEntry = {
							kind: 'file',
							id: event.id,
							name: event.name,
							size: event.size,
							url: getAgentFileUrl(event.url),
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'auth_error': {
					// The harness session lapsed, not the conversation — it is still
					// there, so re-exchange the login and reopen the stream on it
					// rather than telling the user to go and sign in.
					void reauthenticate();
					break;
				}
				case 'idle': {
					endTurn();
					if (event.error) {
						appendNotice('error', event.error);
					} else if (event.silent) {
						appendNotice(
							'info',
							'The model finished without producing a reply — try again, or lower the reasoning level.'
						);
					}
					break;
				}
				case 'thinking_level': {
					setConversation((current) =>
						current ? { ...current, thinkingLevel: event.thinkingLevel } : current
					);
					break;
				}
				case 'model': {
					setConversation((current) => (current ? { ...current, model: event.model } : current));
					break;
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appendNotice, upsertApproval, startTurnClock, endTurn, settleHello]
	);

	const closeStream = useCallback(() => {
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
	}, []);

	const openStream = useCallback(
		(conversationId: string) => {
			closeStream();
			const source = new EventSource(getAgentStreamUrl(conversationId), {
				withCredentials: true,
			});
			source.onmessage = (messageEvent) => {
				let parsed: AgentStreamEvent | null = null;
				try {
					parsed = JSON.parse(messageEvent.data);
				} catch (parseError) {
					parsed = null;
				}
				if (parsed) {
					handleStreamEvent(parsed);
				}
			};
			source.onerror = () => {
				// Transient drops reconnect on their own with Last-Event-ID; a CLOSED
				// source means the server refused the stream — most likely the
				// harness restarted and this session is gone. Recover once, silently.
				if (source.readyState === EventSource.CLOSED && !unmountedRef.current) {
					// Anyone waiting on this stream's `hello` will never get it. Say so
					// now rather than making them sit out the timeout.
					settleHello(new Error('The assistant connection was refused.'));
					void recoverSession();
				}
			};
			eventSourceRef.current = source;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[closeStream, handleStreamEvent, settleHello]
	);

	const startConversation = useCallback(
		async (model?: string, thinkingLevel?: string) => {
			const created = await actions.createConversation(model, thinkingLevel);
			// The panel can be closed, or expanded to fullscreen, while this request
			// is in flight. The mount cleanup has then already run and closed the
			// stream that existed at the time, so opening another one here left an
			// EventSource alive with nothing to close it — holding a harness
			// connection and dispatching events into an abandoned hook.
			if (unmountedRef.current) {
				return created;
			}
			setConversation(created);
			conversationRef.current = created;
			resumeIdRef.current = created.id;
			if (options && options.onConversationId) {
				options.onConversationId(created.id);
			}
			setEntries([]);
			setPendingUploads([]);
			// A message that failed in the previous conversation must not stay
			// retryable — `retry` would deliver it into this one.
			setUnsentMessages({});
			endTurn();
			openStream(created.id);
			return created;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.createConversation, openStream]
	);

	/**
	 * Reopen an existing conversation rather than creating one. The server
	 * replays its whole event log to a stream that sends no Last-Event-ID, and
	 * user messages are in that log, so the full transcript comes back. `hello`
	 * fills in the model and toolset, which is why they start empty here.
	 */
	const resumeConversation = useCallback(
		(conversationId: string) => {
			const resumed = { id: conversationId, model: '', thinkingLevel: '', toolset: '' };
			setConversation(resumed);
			conversationRef.current = resumed;
			resumeIdRef.current = conversationId;
			setEntries([]);
			setPendingUploads([]);
			// A message that failed in the previous conversation must not stay
			// retryable — `retry` would deliver it into this one.
			setUnsentMessages({});
			endTurn();
			openStream(conversationId);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[openStream]
	);

	/**
	 * Re-exchange the login and carry on with the same conversation.
	 *
	 * Harness sessions are in-memory and expire on their own TTL (8h by default),
	 * so a 401 on a call that worked a minute ago usually means only the session
	 * lapsed. `agent_harness_thunks` has always documented this — *"Call it before
	 * anything else, and again on any 401"* — but nothing did it, so a lapsed
	 * session read as a dead assistant.
	 *
	 * A fresh exchange costs one of MAX_SESSIONS (10 by default), which is why
	 * this runs only on a real 401 and never speculatively.
	 */
	const reauthenticate = useCallback(async () => {
		if (recoveringRef.current || unmountedRef.current) {
			return;
		}
		recoveringRef.current = true;
		setStatus('reconnecting');
		try {
			await exchangeLoginOnce();
			if (unmountedRef.current) {
				return;
			}
			if (conversationRef.current) {
				// The reopened stream is what proves the new session can actually see
				// this conversation, so wait for its `hello` rather than assuming.
				const reopened = awaitHello();
				openStream(conversationRef.current.id);
				await reopened;
			}
			setStatus('ready');
			setErrorInfo(null);
		} catch (authError) {
			if (unmountedRef.current) {
				return;
			}
			setStatus('error');
			setErrorInfo(classifyAgentError(authError));
		} finally {
			recoveringRef.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exchangeLoginOnce, openStream, awaitHello]);

	/**
	 * Runs a harness call, and on a 401 re-exchanges the login and runs it once
	 * more. Anything else propagates untouched.
	 *
	 * If the second attempt fails too, the connection strip takes ownership of
	 * the message and the error is marked surfaced, so the caller's catch does
	 * not also drop a duplicate notice into the transcript.
	 */
	const withReauth = useCallback(
		async <T>(run: () => Promise<T>): Promise<T> => {
			try {
				return await run();
			} catch (firstError) {
				if (!isAuthFailure(firstError) || unmountedRef.current) {
					throw firstError;
				}
			}
			setStatus('reconnecting');
			try {
				await exchangeLoginOnce();
			} catch (loginError) {
				// No session at all — the assistant is down, not just this call.
				if (!unmountedRef.current) {
					setStatus('error');
					setErrorInfo(classifyAgentError(loginError));
				}
				throw markSurfaced(loginError);
			}
			try {
				const result = await run();
				if (!unmountedRef.current) {
					setStatus('ready');
					setErrorInfo(null);
				}
				return result;
			} catch (retryError) {
				if (isAuthFailure(retryError)) {
					// A brand-new session was refused too, so the OpenWrench token
					// behind it is dead and nothing here can fix that.
					if (!unmountedRef.current) {
						setStatus('error');
						setErrorInfo(classifyAgentError(retryError));
					}
					throw markSurfaced(retryError);
				}
				// The session is fine and this one call simply failed — keep the
				// assistant usable and let the caller put a notice in the transcript.
				if (!unmountedRef.current) {
					setStatus('ready');
				}
				throw retryError;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[exchangeLoginOnce]
	);

	const recoverSession = useCallback(async () => {
		if (recoveringRef.current || unmountedRef.current) {
			return;
		}
		recoveringRef.current = true;
		setStatus('reconnecting');
		try {
			await exchangeLoginOnce();
			// Same rule as `connect`: the replacement stream has to say `hello`
			// before this counts as recovered. Marking it ready on the strength of
			// a constructed EventSource reopened the composer onto a stream that
			// might itself have been refused — and the next recovery would then
			// clear whatever had been typed into it.
			const recovered = awaitHello();
			await startConversation();
			await recovered;
			appendNotice('info', 'The assistant restarted, so this is a fresh conversation.');
			setStatus('ready');
			setErrorInfo(null);
		} catch (recoveryError) {
			setStatus('error');
			setErrorInfo(classifyAgentError(recoveryError));
		} finally {
			recoveringRef.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exchangeLoginOnce, startConversation, appendNotice, awaitHello]);

	/**
	 * Bring the chat up: resume `resumeId` if one is given, otherwise exchange a
	 * login and start fresh. Separate from the mount effect so the connection
	 * strip's retry button can run it again — a boot failure used to be terminal
	 * for the lifetime of the panel.
	 */
	const connect = useCallback(
		async (resumeId: string): Promise<boolean> => {
			setStatus('connecting');
			setErrorInfo(null);
			try {
				// One probe for both paths, and it asks *who* rather than merely
				// whether. Each login exchange consumes one of MAX_SESSIONS (10 by
				// default), so reusing a good session matters — the cookie is shared
				// across tabs while the conversation id is not, and a second tab that
				// exchanged unconditionally replaced the cookie and stranded the first
				// tab's conversation in an abandoned session.
				//
				// But a working session is not necessarily *this person's* session.
				// The cookie has no Max-Age and the sign-out logout is best effort, so
				// it can outlive the account that created it; `/api/models` would
				// happily answer for the previous user and the new sign-in would then
				// read and write as them. `/api/me` is what makes reuse safe.
				let reusable = false;
				try {
					const harnessUser = await actions.fetchIdentity();
					reusable = !!(
						options &&
						options.canReuseSession &&
						options.canReuseSession(harnessUser.email, harnessUser.role)
					);
				} catch (probeError) {
					// Only a session the server has rejected is worth replacing. This
					// used to catch everything, so one dropped request — offline for a
					// second, a 500, a rate limit — started a fresh chat and stranded a
					// transcript that was still perfectly resumable. Anything else goes
					// to the boot handler, which surfaces a retryable error and leaves
					// `resumeIdRef` intact.
					if (!isSessionUnusable(probeError)) {
						throw probeError;
					}
				}
				if (unmountedRef.current) {
					return false;
				}

				if (!reusable) {
					// Nothing from the old session survives, the stored conversation
					// included, so a retry must not try to reopen it.
					resumeIdRef.current = '';
					await exchangeLoginOnce();
					if (options && options.onSessionEstablished) {
						options.onSessionEstablished();
					}
				}
				const models = await actions.fetchModels();
				if (unmountedRef.current) {
					return false;
				}
				setModelsInfo(models);

				if (reusable && resumeId) {
					// Deliberately NOT ready yet. Going ready here flipped the panel
					// out of "connecting" before the stream had opened, so the empty
					// state rendered its greeting for one frame and the replayed
					// conversation then replaced it. `hello` sets ready instead, and
					// the harness sends it in the same write as the replay, so the
					// transcript and the status change land in one render.
					const resumed = awaitHello();
					resumeConversation(resumeId);
					await resumed;
					return !unmountedRef.current;
				}

				resumeIdRef.current = '';
				// Armed before the stream is opened, and awaited after: returning true
				// on the strength of `openStream` alone said "connected" when all that
				// existed was a constructed EventSource, and `retry` took that as
				// licence to post a message into a conversation the client could not
				// hear back from.
				const connected = awaitHello();
				await startConversation();
				if (unmountedRef.current) {
					settleHello();
					await connected;
					return false;
				}
				await connected;
				return !unmountedRef.current;
			} catch (bootError) {
				if (unmountedRef.current) {
					return false;
				}
				setStatus('error');
				setErrorInfo(classifyAgentError(bootError));
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			exchangeLoginOnce,
			actions.fetchModels,
			actions.fetchIdentity,
			resumeConversation,
			startConversation,
			awaitHello,
			settleHello,
		]
	);

	useMountEffect(() => {
		unmountedRef.current = false;
		void connect(options && options.conversationId ? options.conversationId : '');
		return () => {
			unmountedRef.current = true;
			closeStream();
		};
	});

	/**
	 * Puts one message on the wire under `messageId`.
	 *
	 * Split out from `sendMessage` so a retry can re-send the *same* message
	 * rather than composing a new one: the id is reused, so the row already in
	 * the transcript is the row that updates, and the stream's echo still
	 * de-duplicates against it.
	 *
	 * Reads the conversation from the ref because a retry may have just rebuilt
	 * it, and this callback would still be holding the previous render's value.
	 */
	const deliverMessage = useCallback(
		async (text: string, messageId: string) => {
			const active = conversationRef.current;
			if (!active) {
				return;
			}
			setUnsentMessages((current) => {
				if (!current[messageId]) {
					return current;
				}
				const next = { ...current };
				delete next[messageId];
				return next;
			});
			// Render immediately; the stream echoes it back under the same id, which
			// the user_text handler recognises instead of duplicating. On a retry the
			// row is usually already there, which is why this is an upsert.
			setEntries((current) =>
				current.some((existing) => existing.kind === 'user' && existing.id === messageId)
					? current
					: [...current, { kind: 'user', id: messageId, text }]
			);
			setBusy(true);
			setTurnStartedAt(Date.now());
			try {
				await withReauth(() => actions.sendMessage(active.id, messageId, text));
			} catch (sendError) {
				// A message that never left the browser is not a turn.
				endTurn();
				// No transcript notice here — the message keeps its own row with the
				// reason and a Try again on it, which is more use than a line of red
				// text under something the user has to retype.
				const unsent: AgentUnsentMessage = {
					id: messageId,
					// `classifyAgentError` rather than `humanizeAgentError`: its wording
					// assumes an action sits next to it, so it does not end with an
					// instruction the button is already giving.
					reason: sendError && sendError.surfaced ? '' : classifyAgentError(sendError).message,
					text,
				};
				setUnsentMessages((current) => ({ ...current, [messageId]: unsent }));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.sendMessage, withReauth, endTurn]
	);

	const sendMessage = useCallback(
		async (text: string) => {
			if (!conversationRef.current) {
				return;
			}
			const trimmed = text.trim();
			const uploadMarkers = pendingUploads
				.map((upload) => '[Uploaded file: ' + upload.path + ']')
				.join('\n');
			const fullText = (trimmed + (uploadMarkers ? '\n' + uploadMarkers : '')).trim();
			if (!fullText) {
				return;
			}
			setPendingUploads([]);
			await deliverMessage(fullText, mintMessageId());
		},
		[pendingUploads, deliverMessage]
	);

	/**
	 * The Try again behind both the connection strip and an unsent message.
	 *
	 * Reconnects first if the connection is what broke — from scratch, never via
	 * the stored conversation, since a conversation that would not reopen is
	 * exactly how we got here — and then re-sends the message that did not make
	 * it, so nothing has to be retyped.
	 */
	const retrySend = useCallback(
		(messageId: string) => {
			void (async () => {
				const unsent = unsentMessages[messageId];
				if (!unsent) {
					return;
				}
				if (status === 'error') {
					const connected = await connect(resumeIdRef.current);
					if (!connected) {
						return;
					}
				}
				await deliverMessage(unsent.text, unsent.id);
			})();
		},
		[status, unsentMessages, connect, deliverMessage]
	);

	/**
	 * The connection strip's Try again. Reconnects, then re-sends whatever did
	 * not make it out — the composer is disabled while the connection is down, so
	 * there is at most one such message and it is the last one typed.
	 */
	const retry = useCallback(() => {
		void (async () => {
			if (status === 'error') {
				const connected = await connect(resumeIdRef.current);
				if (!connected) {
					return;
				}
			}
			const ids = Object.keys(unsentMessages);
			const last = ids.length ? unsentMessages[ids[ids.length - 1]] : null;
			if (last) {
				await deliverMessage(last.text, last.id);
			}
		})();
	}, [status, unsentMessages, connect, deliverMessage]);

	/**
	 * Resolves true when the decision reached the harness.
	 *
	 * The card locks both of its buttons the moment one is pressed, so it needs
	 * to know when to unlock again — otherwise a failed decision leaves an
	 * approval that can never be answered.
	 */
	const resolveApproval = useCallback(
		async (approvalId: string, approved: boolean): Promise<boolean> => {
			try {
				await withReauth(() => actions.resolveApproval(approvalId, approved));
				return true;
			} catch (approvalError) {
				appendErrorNotice(approvalError, 'Resolving the approval failed.');
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.resolveApproval, withReauth, appendErrorNotice]
	);

	/**
	 * Resolves true when the turn is known to be stopped.
	 *
	 * It used to swallow everything and return void, which quietly defeated the
	 * caller below: `newConversation` awaited it, saw a fulfilled promise, and
	 * replaced the conversation even when the cancel had failed and the turn was
	 * still running on the server.
	 */
	const cancelTurn = useCallback(async (): Promise<boolean> => {
		if (!conversation) {
			return true;
		}
		try {
			// Through the re-auth ladder like every other write. Swallowing a bare
			// 401 made Stop indistinguishable from "too late": the user pressed it,
			// nothing happened, and the agent carried on.
			await withReauth(() => actions.cancelTurn(conversation.id));
			return true;
		} catch (cancelError) {
			// A turn that has already ended is the one failure worth ignoring: the
			// stream's idle event is authoritative and there is nothing to tell the
			// user. Anything else means the turn may still be running.
			return cancelError && cancelError.status === 404;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conversation, actions.cancelTurn, withReauth]);

	/**
	 * Stop, as the composer's button means it.
	 *
	 * `cancelTurn` reports failure but says nothing, because `newConversation`
	 * needs to decide what to do about it and writes its own message. The button
	 * had no such caller: both composers passed `cancelTurn` straight through and
	 * dropped the result, so a Stop that failed looked exactly like a Stop that
	 * worked while the agent carried on running.
	 */
	const stopTurn = useCallback(async () => {
		if (!(await cancelTurn())) {
			appendNotice('error', 'Could not stop the agent. It may still be running — try again.');
		}
	}, [cancelTurn, appendNotice]);

	const changeModel = useCallback(
		async (model: string) => {
			if (!conversation) {
				return;
			}
			try {
				await withReauth(() => actions.setConversationModel(conversation.id, model));
			} catch (modelError) {
				appendErrorNotice(modelError, 'Switching models failed.');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation, actions.setConversationModel, withReauth, appendErrorNotice]
	);

	const changeThinkingLevel = useCallback(
		async (thinkingLevel: string) => {
			if (!conversation) {
				return;
			}
			try {
				await withReauth(() => actions.setThinkingLevel(conversation.id, thinkingLevel));
			} catch (thinkingError) {
				appendErrorNotice(thinkingError, 'Changing the reasoning level failed.');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation, actions.setThinkingLevel, withReauth, appendErrorNotice]
	);

	const uploadFile = useCallback(
		async (file: File) => {
			setUploading(true);
			try {
				const uploaded = await withReauth(() => actions.uploadFile(file));
				setPendingUploads((current) => [...current, uploaded]);
			} catch (uploadError) {
				appendErrorNotice(uploadError, 'The upload failed.');
			} finally {
				setUploading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.uploadFile, withReauth, appendErrorNotice]
	);

	const removePendingUpload = useCallback((path: string) => {
		setPendingUploads((current) => current.filter((upload) => upload.path !== path));
	}, []);

	const newConversation = useCallback(async () => {
		try {
			// Cancel before replacing. `startConversation` closes the stream and
			// clears busy locally, but the server-side turn would keep running —
			// consuming a slot and possibly reaching an approval nobody can see.
			// If the cancel did not take, leave the conversation alone and say so:
			// abandoning a live turn is worse than not starting a new chat.
			if (busy && !(await cancelTurn())) {
				appendNotice(
					'error',
					'Could not stop the current turn, so this chat was kept. Try again in a moment.'
				);
				return;
			}
			await withReauth(() =>
				startConversation(
					conversation ? conversation.model : undefined,
					conversation ? conversation.thinkingLevel : undefined
				)
			);
		} catch (createError) {
			appendErrorNotice(createError, 'Starting a conversation failed.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		busy,
		cancelTurn,
		conversation,
		startConversation,
		withReauth,
		appendErrorNotice,
		appendNotice,
	]);

	return {
		status,
		errorInfo,
		/** Convenience for callers that only want the text. */
		errorMessage: errorInfo ? errorInfo.message : '',
		unsentMessages,
		turnStartedAt,
		retry,
		retrySend,
		busy,
		entries,
		conversation,
		modelsInfo,
		pendingUploads,
		uploading,
		sendMessage,
		resolveApproval,
		cancelTurn,
		stopTurn,
		changeModel,
		changeThinkingLevel,
		uploadFile,
		removePendingUpload,
		newConversation,
	};
};
